import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { ensureArray } from "../common/utils/arrayUtilities";
import { markFetched } from "../common/hooks/initHook";
import { clientConfig } from "./clientConfigSlice";

const key = a => a.id;

export const awardSlice = createSlice({
    name: 'awards',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            state.data = ensureArray(action.payload).reduce((obj, a) => { obj[key(a)] = a; return obj }, {});
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(a => state.data[key(a)] = a);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(a => delete state.data[key(a)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => awardSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'payAward_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'payAward_one');

const fetchAwards = network => network.get('/api/v1/awards', awardSlice.actions.set, visualizeFailure('get'));

export const createAward = (network, award) => network.post('/api/v1/award', award, visualizeSuccess('create', awardSlice.actions.addOrUpdate), visualizeFailure('create'));
export const updateAward = (network, delta) => network.put('/api/v1/award', delta, awardSlice.actions.addOrUpdate, visualizeFailure('update'));
export const removeAward = (network, id) => network.delete('/api/v1/award/' + encodeURIComponent(id), awardSlice.actions.remove, visualizeFailure('remove'));
// #endregion

// #region selectors
const selectAwardData = state => state.awards.data;
const selectAwards = createSelector([selectAwardData], data => Object.values(data));
const selectAward = id => createSelector([selectAwardData], awards => Object.values(awards).find(a => a.id === id));
// #endregion

const listen = {
    awardCreated: (award, { dispatch, websocket }) => {
        markFetched(websocket, 'award', key(award))
        dispatch(awardSlice.actions.addOrUpdate(award))
    },
    awardUpdated: (award, { dispatch }) => dispatch(awardSlice.actions.addOrUpdate(award)),
    awardRemoved: (award, { dispatch }) => dispatch(awardSlice.actions.remove(award))
}

const initAwards = { run: fetchAwards, listen, immortal: true };
export const [useAward, realizeAward] = dataHookFactory(selectAward, id => initAwards);
export const [useAwards, realizeAwards] = dataHookFactory(selectAwards, initAwards);

export const useAwardMap = () => {
    const awards = useAwards();
    return Object.fromEntries(awards.map(({ id, ...rest }) => [id, rest]));
}

export const realizeAwardMap = async (realizationContext) => {
    const awards = await realizeAwards(realizationContext);
    return Object.fromEntries(awards.map(({ id, ...rest }) => [id, rest]))
}

const fullRoles = ['administrator']
const readRoles = ['auditor']
export const canUpdateAward = (credentials) => credentials.hasRole(fullRoles);
export const canCreateAward = canUpdateAward;
export const canRemoveAward = canUpdateAward;
export const canRouteAwards = ({credentials, clientConfig}) =>  (credentials.hasRole(fullRoles) || credentials.hasRole(readRoles)) && clientConfig.enableAwards;

const reducer = { name: awardSlice.name, reducer: awardSlice.reducer };
export default reducer;